/**
 * Handle for a single logical conversation with the agent.
 *
 * `done()` resolves with the agent's `AgentOutput` (status `final_response`)
 * or rejects with the `AgentError` content. `sendFeedback(value)`
 * answers the most recent `human_input_request` for this run by sending a
 * `user_reply` carrying the matching `reply_id`. Multi-turn HITL stays
 * inside one `Run` until the agent emits a terminal event.
 */
export class Run {
    constructor(thread) {
        this.thread = thread;
        /**
         * The outbound `ref_id` generated by the SDK when the originating
         * `user_input` was pushed. Empty until the push promise resolves.
         */
        this.refId = "";
        this.latestHumanInputRef = null;
        this.humanInputTimer = null;
        this.closed = false;
        this.donePromise = new Promise((res, rej) => {
            this.resolveDone = res;
            this.rejectDone = rej;
        });
    }
    /** @internal — set by AgentThread once the push promise resolves. */
    _setRefId(refId) {
        this.refId = refId;
    }
    /** @internal — feed an inbound event to this run. */
    _push(evt) {
        if (this.closed)
            return;
        switch (evt.event) {
            case "human_input_request": {
                const content = evt.content;
                // Prefer the inbound message's own ref_id; fall back to its
                // explicit reply_to when ref_id is missing.
                this.latestHumanInputRef =
                    content.ref_id || content.reply_to || null;
                // Phase 7: client-side timeout enforcement. If the agent advertises
                // a `timeout` (seconds), reject `done()` with HUMAN_INPUT_TIMEOUT
                // when the user hasn't responded in time. Cleared on next event or
                // on sendFeedback.
                this.clearHumanInputTimer();
                const timeoutSec = content.timeout;
                if (typeof timeoutSec === "number" && timeoutSec > 0) {
                    this.humanInputTimer = setTimeout(() => {
                        if (this.closed)
                            return;
                        const err = {
                            type: "agent_error_response",
                            status: "error",
                            code: "HUMAN_INPUT_TIMEOUT",
                            message: `No response received within ${timeoutSec}s for human_input_request.`,
                            details: { prompt: content.prompt, timeout: timeoutSec },
                            thread_id: content.thread_id || "",
                            ref_id: "",
                            agent_id: content.agent_id || "",
                            reply_to: this.latestHumanInputRef || "",
                        };
                        this.closed = true;
                        this.rejectDone(err);
                        this.thread._closeRun(this);
                    }, timeoutSec * 1000);
                }
                this.thread._fireRequestFeedback(content, this);
                break;
            }
            case "agent_output": {
                this.clearHumanInputTimer();
                this.closed = true;
                this.resolveDone(evt.content);
                this.thread._closeRun(this);
                break;
            }
            case "agent_error": {
                this.clearHumanInputTimer();
                this.closed = true;
                this.rejectDone(evt.content);
                this.thread._closeRun(this);
                break;
            }
            default:
                // agent_wait / planner_correction / unknown — do not terminate.
                break;
        }
    }
    clearHumanInputTimer() {
        if (this.humanInputTimer !== null) {
            clearTimeout(this.humanInputTimer);
            this.humanInputTimer = null;
        }
    }
    /**
     * Resolves on the agent's terminal `agent_output`, rejects on `agent_error`
     * (rejected with the typed `AgentError` content) or transport-level errors.
     */
    done() {
        return this.donePromise;
    }
    /**
     * Send a reply to the most recent `human_input_request`. Throws if no
     * input is currently pending. The reply is sent as `user_reply` with
     * `reply_id` set to the originating request's `ref_id`.
     */
    async sendFeedback(value) {
        const replyId = this.latestHumanInputRef;
        if (!replyId) {
            throw new Error("Run.sendFeedback: no pending human_input_request to respond to.");
        }
        // Consume the pending input *before* sending so a reentrant handler
        // doesn't double-respond.
        this.latestHumanInputRef = null;
        this.clearHumanInputTimer();
        await this.thread._sendReply(value, replyId);
    }
    /** @internal — called by AgentThread when the run is force-closed. */
    _close(reason = "Run closed before terminal event") {
        if (this.closed)
            return;
        this.clearHumanInputTimer();
        this.closed = true;
        this.rejectDone(new Error(reason));
    }
    /** Whether the run has terminated. */
    isClosed() {
        return this.closed;
    }
}
